/* WeQuote Dev Board — the suggested-priority / triage engine (PRD §5.4).
 * Ported verbatim: score = round(100·(0.4·priority + 0.4·demand + 0.2·effort));
 * demand falls back to the priority score when no requests are logged. Two
 * bands — Committed/scheduled (Design Complete onward, ordered by est.
 * completion) above the Triage backlog (Needs-score order); blockers float to
 * the top of triage; pins lock a slot and unpinned cards reflow around them. */
import type { Feature } from './types'
import { DEVCOLS, isPool } from './constants'
import { reqCount, inPipelineF, isDesignOverdue, isDevOverdue, isDesignStartSoon } from './logic'
import { daysUntil } from './dates'
import type { MutResult } from './mutations'

export const SUG_PRI_W = 0.4
export const SUG_DEM_W = 0.4
export const SUG_EFF_W = 0.2
export const SUG_DEM_CAP = 5

export function priScoreV(f: Feature): number { return f.pri === 'high' ? 1 : f.pri === 'medium' ? 0.6 : 0.3 }
export function effScoreV(f: Feature): number { return f.eff === 'quickwin' || f.eff === 'low' ? 1 : f.eff === 'medium' ? 0.6 : 0.3 }
export function demScoreV(f: Feature): number {
  const c = reqCount(f)
  return c <= 0 ? priScoreV(f) : Math.min(c, SUG_DEM_CAP) / SUG_DEM_CAP
}
export function suggestScore(f: Feature): number {
  return Math.round(100 * (SUG_PRI_W * priScoreV(f) + SUG_DEM_W * demScoreV(f) + SUG_EFF_W * effScoreV(f)))
}
export function suggestBreakdown(f: Feature): string {
  const c = reqCount(f)
  const pr = f.pri === 'high' ? 'High' : f.pri === 'medium' ? 'Medium' : 'Low'
  const dm = c > 0 ? c + ' compan' + (c === 1 ? 'y' : 'ies') + ' asking' : 'no demand logged (uses priority)'
  const ef = f.eff === 'quickwin' ? 'quick win' : f.eff === 'low' ? 'low effort' : f.eff === 'medium' ? 'medium effort' : 'big job'
  return pr + ' priority · ' + dm + ' · ' + ef
}

export function isCommitted(f: Feature): boolean {
  return f.status === 'designcomplete' || DEVCOLS.indexOf(f.status) > -1 || f.status === 'custreview' || f.status === 'testing' || f.status === 'testcomplete' || f.status === 'onhold'
}
export function isTriage(f: Feature): boolean {
  return isPool(f.status) || f.status === 'design' // pool bands + design (v2 §1)
}

export function committedOrder(list: Feature[]): Feature[] {
  return list.slice().sort((a, b) => {
    const ae = a.estCompletion || '9999-99'
    const be = b.estCompletion || '9999-99'
    if (ae !== be) return ae < be ? -1 : 1
    return a.po - b.po
  })
}
/** Which triage band a card sits in (design above the pool, then Now → Next → Later). */
export function triageBand(f: Feature): string {
  const st = f.status
  if (st === 'design') return 'design'
  if (st === 'now') return 'now'
  if (st === 'next') return 'next'
  return 'later'
}
/** Order a SINGLE band: blockers on top, pins hold their slot, the rest by score. */
export function triageBandOrder(list: Feature[]): Feature[] {
  const scored = list.slice().sort((a, b) => suggestScore(b) - suggestScore(a) || a.po - b.po)
  const blockers = scored.filter((f) => f.blocker)
  const rest = scored.filter((f) => !f.blocker)
  const pinned = rest.filter((f) => f.triagePinned).sort((a, b) => (a.triagePos == null ? 0 : a.triagePos) - (b.triagePos == null ? 0 : b.triagePos))
  const unpinned = rest.filter((f) => !f.triagePinned)
  const slot: Record<number, Feature> = {}
  const used: Record<number, 1> = {}
  pinned.forEach((f) => {
    let idx = f.triagePos == null ? 0 : f.triagePos
    if (idx < 0) idx = 0
    if (idx > rest.length - 1) idx = rest.length - 1
    let guard = 0
    while (used[idx] && guard < rest.length) { idx = (idx + 1) % rest.length; guard++ }
    used[idx] = 1
    slot[idx] = f
  })
  const out: Feature[] = []
  let ui = 0
  for (let i = 0; i < rest.length; i++) out.push(slot[i] !== undefined ? slot[i] : unpinned[ui++])
  return blockers.concat(out.filter((x) => x !== undefined))
}
/** v2 §1: triage groups by band (design → Now → Next → Later), score within each. */
export function triageOrder(list: Feature[]): Feature[] {
  let out: Feature[] = []
  ;['design', 'now', 'next', 'later'].forEach((band) => {
    out = out.concat(triageBandOrder(list.filter((f) => triageBand(f) === band)))
  })
  return out
}

export function suggestedOrder(items: Feature[]): Feature[] {
  const committed = committedOrder(items.filter(isCommitted))
  const triage = triageOrder(items.filter(isTriage))
  const other = items.filter((f) => !isCommitted(f) && !isTriage(f)).sort((a, b) => a.po - b.po)
  return committed.concat(triage, other)
}
export function suggestRankMap(features: Feature[]): Record<number, number> {
  const m: Record<number, number> = {}
  suggestedOrder(features.filter(inPipelineF)).forEach((f, i) => { m[f.id] = i + 1 })
  return m
}
export function suggestTriagePos(feat: Feature, features: Feature[]): { pos: number; total: number } {
  const triage = triageOrder(features.filter((x) => inPipelineF(x) && isTriage(x)))
  return { pos: triage.findIndex((x) => x.id === feat.id) + 1, total: triage.length }
}
export function currentTriagePos(feat: Feature, features: Feature[]): number {
  const triage = features.filter((x) => inPipelineF(x) && isTriage(x)).sort((a, b) => a.po - b.po)
  return triage.findIndex((x) => x.id === feat.id) + 1
}

/* ── pins / blockers / apply (pure mutations) ── */
export function pinDefaultPos(id: number, features: Feature[]): number {
  const triage = triageOrder(features.filter((x) => inPipelineF(x) && isTriage(x)))
  const idx = triage.findIndex((x) => x.id === id)
  return idx < 0 ? 0 : idx
}
export function confirmPin(fs: Feature[], id: number, pos: number): MutResult {
  return {
    features: fs.map((f) => (f.id === id ? { ...f, triagePinned: true, triagePos: pos, blocker: false } : f)),
    toast: 'Position locked — suggested cards will arrange around it.',
    changedIds: [id],
  }
}
export function unpinCard(fs: Feature[], id: number): MutResult {
  return {
    features: fs.map((f) => (f.id === id ? { ...f, triagePinned: false, triagePos: null } : f)),
    toast: 'Card unpinned.',
    changedIds: [id],
  }
}
export function nudgePin(fs: Feature[], id: number, dir: number): MutResult {
  const triageCount = fs.filter((x) => inPipelineF(x) && isTriage(x) && !x.blocker).length
  return {
    features: fs.map((f) => {
      if (f.id !== id || !f.triagePinned) return f
      let p = (f.triagePos == null ? 0 : f.triagePos) + dir
      if (p < 0) p = 0
      if (p > triageCount - 1) p = triageCount - 1
      return { ...f, triagePos: p }
    }),
    changedIds: [id],
  }
}
export function markBlocker(fs: Feature[], id: number): MutResult {
  let on = false
  let title = ''
  const next = fs.map((f) => {
    if (f.id !== id) return f
    const blocker = !f.blocker
    on = blocker
    title = f.title
    return blocker ? { ...f, blocker, triagePinned: false, triagePos: null } : { ...f, blocker }
  })
  return { features: next, toast: on ? '“' + title + '” marked as a blocker — pinned to the top.' : 'Blocker removed.', changedIds: [id] }
}
export function resetSuggestions(fs: Feature[]): MutResult {
  const ids = fs.filter((f) => f.triagePinned || f.blocker || f.triagePos != null).map((f) => f.id)
  return {
    features: fs.map((f) => ({ ...f, triagePinned: false, triagePos: null, blocker: false })),
    toast: 'All pins and blockers removed; suggested layout reset.',
    changedIds: ids,
  }
}
/** Apply the suggested layout to po. (The scheduler refresh joins in the
 *  Roadmap phase — committed ordering falls back to po until estCompletion
 *  is computed.) Returns an undo snapshot alongside. */
export function applySuggested(fs: Feature[]): { result: MutResult; undo: { id: number; po: number }[] } {
  const undo = fs.map((f) => ({ id: f.id, po: f.po }))
  const order = suggestedOrder(fs.filter(inPipelineF))
  const rest = fs.filter((f) => !inPipelineF(f)).sort((a, b) => a.po - b.po)
  const all = order.concat(rest).map((f, i) => ({ ...f, po: i + 1 }))
  return { result: { features: all, changedIds: all.map((f) => f.id) }, undo }
}
export function undoApplySuggested(fs: Feature[], undo: { id: number; po: number }[]): MutResult {
  const map: Record<number, number> = {}
  undo.forEach((s) => { map[s.id] = s.po })
  const all = fs.map((f) => (map[f.id] !== undefined ? { ...f, po: map[f.id] } : f))
  return { features: all, toast: 'Reverted to the previous order.', changedIds: all.map((f) => f.id) }
}
export function reorder(fs: Feature[], fromId: number, toId: number): MutResult {
  const s = fs.slice().sort((a, b) => a.po - b.po)
  const fi = s.findIndex((f) => f.id === fromId)
  const ti = s.findIndex((f) => f.id === toId)
  if (fi < 0 || ti < 0) return { features: fs }
  const m = s.splice(fi, 1)[0]
  s.splice(ti, 0, m)
  const all = s.map((f, i) => ({ ...f, po: i + 1 }))
  return { features: all, changedIds: all.map((f) => f.id) }
}

/* ── sorting + filters (pure fns of explicit state) ── */
export type SortMode = 'priority' | 'suggested' | 'requests' | 'quickwins' | 'effort'
export function applySort(items: Feature[], sortMode: SortMode): Feature[] {
  const arr = items.slice()
  if (sortMode === 'requests') {
    arr.sort((a, b) => reqCount(b) - reqCount(a) || a.po - b.po)
  } else if (sortMode === 'quickwins') {
    arr.sort((a, b) => (a.eff === 'quickwin' ? 0 : 1) - (b.eff === 'quickwin' ? 0 : 1) || a.po - b.po)
  } else if (sortMode === 'effort') {
    const ord: Record<string, number> = { quickwin: 0, low: 1, medium: 2, high: 3 }
    arr.sort((a, b) => (ord[a.eff] == null ? 9 : ord[a.eff]) - (ord[b.eff] == null ? 9 : ord[b.eff]) || a.po - b.po)
  } else if (sortMode === 'suggested') {
    return suggestedOrder(arr)
  } else {
    // Priority sort (Lee, 2 Sep): ONE grouped flow — Committed (by projected
    // completion, the order they'll actually be delivered) → Design → Now →
    // Next → Later (each by manual order). Never by the raw stored po alone.
    return groupedOrder(arr)
  }
  return arr
}

/** The canonical grouped flow for the Priority list (Lee, 2 Sep). */
export function groupedOrder(items: Feature[]): Feature[] {
  const committed = committedOrder(items.filter(isCommitted))
  const byPo = (a: Feature, b: Feature) => a.po - b.po
  const design = items.filter((f) => f.status === 'design').sort(byPo)
  const now = items.filter((f) => f.status === 'now').sort(byPo)
  const nxt = items.filter((f) => f.status === 'next').sort(byPo)
  const later = items.filter((f) => isPool(f.status) && f.status !== 'now' && f.status !== 'next').sort(byPo)
  const other = items.filter((f) => !isCommitted(f) && f.status !== 'design' && !isPool(f.status)).sort(byPo)
  return committed.concat(design, now, nxt, later, other)
}

/** Advisory band from the suggested score (Lee, 2 Sep): flags that a pool
 *  card LOOKS like it belongs higher/lower. Never moves anything. */
export function suggestBand(f: Feature): 'now' | 'next' | 'later' {
  const sc = suggestScore(f)
  return sc >= 70 ? 'now' : sc >= 45 ? 'next' : 'later'
}

export interface ToolbarFilters { pri: string; dev: string; design: string; bug: string; status?: string; reqBy?: string; modules?: string[]; band?: string }
/* reqBy (Lee, 28 Aug): 'c:<company>' or 'p:<person>' — card passes when any
 * of its requests matches that requester. 'all'/absent = no restriction. */
/* status filter (Lee, 27 Aug): every kanban column is a selectable status. */
export function toolbarFilter(fl: ToolbarFilters) {
  return (f: Feature): boolean => {
    let pass = (fl.pri === 'all' || f.pri === fl.pri) && (fl.dev === 'all' || f.dev === fl.dev)
    if (fl.status && fl.status !== 'all') pass = pass && f.status === fl.status
    if (fl.band && fl.band !== 'all') pass = pass && f.status === fl.band // Now/Next/Later band filter (v2 §1)
    if (fl.modules && fl.modules.length > 0) pass = pass && fl.modules.indexOf(f.module || '') > -1
    if (fl.reqBy && fl.reqBy !== 'all') {
      const t = fl.reqBy.slice(2).toLowerCase()
      const byCompany = fl.reqBy[0] === 'c'
      pass = pass && (f.requests || []).some((r) =>
        byCompany ? (r.company || '').trim().toLowerCase() === t : (r.person || '').trim().toLowerCase() === t)
    }
    if (fl.design === 'yes') pass = pass && !!f.requiresDesign
    else if (fl.design === 'no') pass = pass && !f.requiresDesign
    if (fl.bug === 'bugs') pass = pass && !!f.isBug
    else if (fl.bug === 'nobugs') pass = pass && !f.isBug
    return pass
  }
}
export type FocusFilter = 'all' | 'overdue' | 'duesoon' | 'design-overdue' | 'dev-overdue' | 'design-soon'
export function focusFilterFn(focus: FocusFilter) {
  return (f: Feature): boolean => {
    switch (focus) {
      case 'overdue': return isDevOverdue(f) || isDesignOverdue(f)
      case 'duesoon': { const d = daysUntil(f.deadline); return d !== null && d >= 0 && d <= 7 }
      case 'design-overdue': return isDesignOverdue(f)
      case 'dev-overdue': return isDevOverdue(f)
      case 'design-soon': return isDesignStartSoon(f)
      default: return true
    }
  }
}

/* ── v2 §1: Organise pool — re-sort each band by suggested score (blockers
 * first, pins hold), reusing the band's existing po slots. Undoable via the
 * same undo shape as applySuggested. ── */
export function organisePool(fs: Feature[]): { result: MutResult; undo: { id: number; po: number }[] } | { toast: string } {
  const undo = fs.map((f) => ({ id: f.id, po: f.po }))
  const pool = fs.filter((f) => inPipelineF(f) && isTriage(f))
  if (!pool.length) return { toast: 'Nothing in the pool to organise.' }
  const slots = pool.map((f) => f.po).sort((a, b) => a - b)
  const po: Record<number, number> = {}
  triageOrder(pool).forEach((f, i) => { po[f.id] = slots[i] })
  const next = fs.map((f) => (po[f.id] !== undefined ? { ...f, po: po[f.id] } : f))
  const changedIds = next.filter((f, i) => f.po !== fs[i].po).map((f) => f.id)
  return { result: { features: next, changedIds }, undo }
}

/* ── v2 §1: a card entering the pool lands at its scored position WITHIN its
 * band (po midpoints), not at the bottom of 200. ── */
export function insertByScore(fs: Feature[], id: number): Feature[] {
  const f = fs.find((x) => x.id === id)
  if (!f) return fs
  const pool = fs.filter((x) => x.id !== id && x.status === f.status)
  if (!pool.length) return fs
  const sc = suggestScore(f)
  const above = pool.filter((x) => suggestScore(x) >= sc)
  const below = pool.filter((x) => suggestScore(x) < sc)
  const pos = pool.map((x) => x.po)
  let po: number
  if (!above.length) po = Math.min(...pos) - 1
  else if (!below.length) po = Math.max(...pos) + 1
  else po = (Math.max(...above.map((x) => x.po)) + Math.min(...below.map((x) => x.po))) / 2
  return fs.map((x) => (x.id === id ? { ...x, po } : x))
}
